<template>
    <app-layout>
        <template #title>{{ $t("kiosk.api_logs.title") }}</template>
        <template #description>{{ $t("kiosk.api_logs.description") }}</template>

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto">
            <!-- User Banner -->
            <div
                v-if="props.filterUser"
                class="bg-indigo-50 border-indigo-200 rounded-xl mb-6 px-5 py-3.5 flex items-center justify-between border"
            >
                <div class="gap-2 text-sm flex items-center">
                    <User class="h-4 w-4 text-indigo-500" />
                    <span class="text-indigo-700">
                        {{
                            $t("kiosk.api_logs.user_filter_label", {
                                name: props.filterUser.name,
                                email: props.filterUser.email,
                            })
                        }}
                    </span>
                </div>
                <inertia-link
                    :href="route('app.kiosk.api-logs.index')"
                    class="px-3 py-1 text-xs font-medium rounded-md text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 transition-colors"
                >
                    {{ $t("kiosk.api_logs.clear_user_filter") }}
                </inertia-link>
            </div>

            <!-- Filters -->
            <div
                class="bg-white rounded-xl border-iron-200 mb-6 overflow-hidden border"
            >
                <!-- Filter Header -->
                <button
                    @click="filtersExpanded = !filtersExpanded"
                    class="px-5 py-3.5 hover:bg-iron-50 flex w-full items-center justify-between transition-colors"
                >
                    <div class="gap-2.5 flex items-center">
                        <div
                            class="w-7 h-7 rounded-md bg-iron-100 flex items-center justify-center"
                        >
                            <SlidersHorizontal
                                class="h-3.5 w-3.5 text-iron-500"
                            />
                        </div>
                        <span class="text-sm font-medium text-iron-700">{{
                            $t("kiosk.api_logs.filters")
                        }}</span>
                        <span
                            v-if="activeFilterCount > 0"
                            class="font-semibold px-1.5 py-0.5 rounded-md bg-iron-900 text-white text-[11px] tabular-nums"
                            >{{ activeFilterCount }}</span
                        >
                    </div>
                    <ChevronDown
                        :class="[
                            'h-4 w-4 text-iron-400 transition-transform duration-200',
                            filtersExpanded ? 'rotate-180' : '',
                        ]"
                    />
                </button>

                <!-- Filter Body -->
                <div
                    v-show="filtersExpanded"
                    class="px-5 pb-5 border-iron-100 border-t"
                >
                    <!-- Row 1: Dates, Method, Status -->
                    <div
                        class="gap-3 sm:grid-cols-3 lg:grid-cols-5 pt-4 grid grid-cols-2"
                    >
                        <div class="gap-1 grid">
                            <Label
                                for="start_date"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{ $t("kiosk.api_logs.start_date") }}</Label
                            >
                            <Input
                                id="start_date"
                                v-model="filterForm.start_date"
                                type="datetime-local"
                                class="h-9 text-sm"
                            />
                        </div>

                        <div class="gap-1 grid">
                            <Label
                                for="end_date"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{ $t("kiosk.api_logs.end_date") }}</Label
                            >
                            <Input
                                id="end_date"
                                v-model="filterForm.end_date"
                                type="datetime-local"
                                class="h-9 text-sm"
                            />
                        </div>

                        <div class="gap-1 grid">
                            <Label
                                for="method"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{ $t("kiosk.api_logs.method") }}</Label
                            >
                            <Select v-model="filterForm.method">
                                <SelectTrigger class="h-9 text-sm w-full">
                                    <SelectValue
                                        :placeholder="
                                            $t('kiosk.api_logs.all_methods')
                                        "
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="GET">GET</SelectItem>
                                    <SelectItem value="POST">POST</SelectItem>
                                    <SelectItem value="PUT">PUT</SelectItem>
                                    <SelectItem value="PATCH">PATCH</SelectItem>
                                    <SelectItem value="DELETE"
                                        >DELETE</SelectItem
                                    >
                                </SelectContent>
                            </Select>
                        </div>

                        <div class="gap-1 grid">
                            <Label
                                for="status_min"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{ $t("kiosk.api_logs.status_min") }}</Label
                            >
                            <Input
                                id="status_min"
                                v-model="filterForm.status_min"
                                type="number"
                                placeholder="100"
                                min="100"
                                max="599"
                                class="h-9 text-sm"
                            />
                        </div>

                        <div class="gap-1 grid">
                            <Label
                                for="status_max"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{ $t("kiosk.api_logs.status_max") }}</Label
                            >
                            <Input
                                id="status_max"
                                v-model="filterForm.status_max"
                                type="number"
                                placeholder="599"
                                min="100"
                                max="599"
                                class="h-9 text-sm"
                            />
                        </div>
                    </div>

                    <!-- Row 2: URL, Token, Correlation ID, Message Search -->
                    <div
                        class="gap-3 sm:grid-cols-2 lg:grid-cols-4 mt-3 grid grid-cols-1"
                    >
                        <div class="gap-1 grid content-start">
                            <Label
                                for="url_search"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{ $t("kiosk.api_logs.url_search") }}</Label
                            >
                            <Select v-model="urlSelectValue">
                                <SelectTrigger
                                    class="h-9 text-sm font-mono w-full"
                                >
                                    <SelectValue
                                        :placeholder="
                                            $t('kiosk.api_logs.all_urls')
                                        "
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem
                                        v-for="r in apiRoutes"
                                        :key="r.value"
                                        :value="r.value"
                                    >
                                        <span class="font-mono text-xs">{{
                                            r.label
                                        }}</span>
                                    </SelectItem>
                                    <SelectSeparator />
                                    <SelectItem value="__custom__">
                                        {{ $t("kiosk.api_logs.custom_url") }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <Input
                                v-if="selectedRouteHasParam"
                                v-model="urlParamValue"
                                type="text"
                                :placeholder="
                                    $t('kiosk.api_logs.param_placeholder')
                                "
                                class="h-9 text-sm font-mono"
                            />
                            <Input
                                v-if="urlSelectValue === '__custom__'"
                                v-model="customUrlValue"
                                type="text"
                                placeholder="/api/..."
                                class="h-9 text-sm font-mono"
                            />
                        </div>

                        <div class="gap-1 grid content-start">
                            <Label
                                for="token"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{ $t("kiosk.api_logs.token") }}</Label
                            >
                            <Input
                                id="token"
                                v-model="filterForm.token"
                                type="text"
                                :placeholder="
                                    $t('kiosk.api_logs.token_placeholder')
                                "
                                class="h-9 text-sm font-mono"
                            />
                        </div>

                        <div class="gap-1 grid content-start">
                            <Label
                                for="correlation_id"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{
                                    $t("kiosk.api_logs.correlation_id")
                                }}</Label
                            >
                            <Input
                                id="correlation_id"
                                v-model="filterForm.correlation_id"
                                type="text"
                                :placeholder="
                                    $t(
                                        'kiosk.api_logs.correlation_id_placeholder',
                                    )
                                "
                                class="h-9 text-sm font-mono"
                            />
                        </div>

                        <div class="gap-1 grid content-start">
                            <Label
                                for="message_search"
                                class="tracking-wider font-medium text-iron-400 text-[11px] uppercase"
                                >{{
                                    $t("kiosk.api_logs.message_search")
                                }}</Label
                            >
                            <Input
                                id="message_search"
                                v-model="filterForm.message_search"
                                type="text"
                                :placeholder="
                                    $t(
                                        'kiosk.api_logs.message_search_placeholder',
                                    )
                                "
                                class="h-9 text-sm font-mono"
                            />
                        </div>
                    </div>

                    <div
                        class="mt-4 pt-3 border-iron-100 gap-2 flex items-center justify-end border-t"
                    >
                        <button
                            @click="clearFilters"
                            class="px-3 py-1.5 text-xs font-medium rounded-md text-iron-500 hover:text-iron-700 hover:bg-iron-100 transition-colors"
                        >
                            {{ $t("kiosk.api_logs.clear") }}
                        </button>
                        <button
                            @click="applyFilters"
                            class="gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-md bg-iron-900 text-white hover:bg-iron-800 inline-flex items-center transition-colors"
                        >
                            <Search class="h-3 w-3" />
                            {{ $t("kiosk.api_logs.search") }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Logs Table -->

            <!-- Skeleton: only on initial/fresh load -->
            <div
                v-if="isInitialLoad && accumulatedEntries.length === 0"
                class="bg-white rounded-xl border-iron-200 overflow-hidden border"
            >
                <div class="divide-iron-100 divide-y">
                    <div
                        v-for="i in 10"
                        :key="i"
                        class="px-5 py-3 gap-4 flex items-center"
                    >
                        <div
                            class="h-2.5 w-28 bg-iron-100 rounded-sm animate-pulse"
                        ></div>
                        <div
                            class="h-5 w-11 bg-iron-100 rounded animate-pulse"
                        ></div>
                        <div
                            class="h-2.5 bg-iron-100 rounded-sm animate-pulse flex-1"
                        ></div>
                        <div
                            class="h-5 w-9 bg-iron-100 animate-pulse rounded-full"
                        ></div>
                        <div
                            class="h-2.5 w-14 bg-iron-100 rounded-sm animate-pulse"
                        ></div>
                    </div>
                </div>
            </div>

            <!-- Results -->
            <div v-else-if="accumulatedEntries.length > 0">
                <!-- Results count -->
                <div class="mb-2 px-1 flex items-center justify-between">
                    <span class="text-xs text-iron-400 tabular-nums"
                        >{{ accumulatedEntries.length }}
                        {{ $t("kiosk.api_logs.results") }}</span
                    >
                </div>

                <!-- Table -->
                <div
                    class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                >
                    <table class="w-full">
                        <thead>
                            <tr class="border-iron-100 border-b">
                                <th
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                >
                                    {{ $t("kiosk.api_logs.table.timestamp") }}
                                </th>
                                <th
                                    class="px-3 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                >
                                    {{ $t("kiosk.api_logs.table.method") }}
                                </th>
                                <th
                                    class="px-3 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                >
                                    {{ $t("kiosk.api_logs.table.url") }}
                                </th>
                                <th
                                    class="px-3 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                >
                                    {{ $t("kiosk.api_logs.table.status") }}
                                </th>
                                <th
                                    class="px-3 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                >
                                    {{ $t("kiosk.api_logs.table.duration") }}
                                </th>
                                <th
                                    class="px-3 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                >
                                    {{ $t("kiosk.api_logs.table.ip") }}
                                </th>
                                <th
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                >
                                    {{ $t("kiosk.api_logs.table.token") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody class="divide-iron-50 divide-y">
                            <tr
                                v-for="(entry, index) in accumulatedEntries"
                                :key="index"
                                @click="openDetail(entry)"
                                class="hover:bg-iron-50/70 group cursor-pointer transition-colors"
                            >
                                <td class="px-5 py-2.5 whitespace-nowrap">
                                    <span
                                        class="text-iron-500 font-mono text-[13px] tabular-nums"
                                        >{{
                                            formatTimestamp(entry.timestamp)
                                        }}</span
                                    >
                                </td>
                                <td class="px-3 py-2.5 whitespace-nowrap">
                                    <span
                                        :class="methodPillClass(entry.method)"
                                        class="font-bold px-2 rounded tracking-wide inline-block py-[3px] text-[11px]"
                                        >{{ entry.method }}</span
                                    >
                                </td>
                                <td class="px-3 py-2.5 max-w-0">
                                    <span
                                        class="text-iron-800 font-mono block truncate text-[13px]"
                                        :title="formatUrl(entry.url)"
                                        >{{ formatUrl(entry.url) }}</span
                                    >
                                </td>
                                <td
                                    class="px-3 py-2.5 text-center whitespace-nowrap"
                                >
                                    <span
                                        v-if="entry.status"
                                        :class="statusPillClass(entry.status)"
                                        class="font-bold px-2 inline-block min-w-[36px] rounded-full py-[3px] text-[11px] tabular-nums"
                                        >{{ entry.status }}</span
                                    >
                                    <span
                                        v-else
                                        class="text-iron-400 text-[13px]"
                                        >—</span
                                    >
                                </td>
                                <td
                                    class="px-3 py-2.5 text-right whitespace-nowrap"
                                >
                                    <span
                                        v-if="entry.duration_ms"
                                        class="text-iron-500 font-mono text-[13px] tabular-nums"
                                        >{{ entry.duration_ms }}ms</span
                                    >
                                    <span
                                        v-else
                                        class="text-iron-400 text-[13px]"
                                        >—</span
                                    >
                                </td>
                                <td class="px-3 py-2.5 whitespace-nowrap">
                                    <span
                                        class="text-iron-400 font-mono text-[13px]"
                                        >{{ entry.ip ?? "—" }}</span
                                    >
                                </td>
                                <td class="px-5 py-2.5 max-w-[120px]">
                                    <span
                                        class="text-iron-400 font-mono block truncate text-[13px]"
                                        :title="entry.token ?? undefined"
                                        >{{ entry.token_short ?? "—" }}</span
                                    >
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Load More -->
                    <div
                        v-if="currentNextToken"
                        class="px-5 py-3 border-iron-100 flex justify-center border-t"
                    >
                        <button
                            @click="loadMore"
                            :disabled="isLoadingMore"
                            class="gap-1.5 px-4 py-1.5 text-xs font-medium rounded-md text-iron-600 hover:text-iron-800 bg-iron-50 hover:bg-iron-100 inline-flex items-center transition-colors disabled:opacity-50"
                        >
                            <Loader2
                                v-if="isLoadingMore"
                                class="h-3 w-3 animate-spin"
                            />
                            <ChevronDown v-else class="h-3 w-3" />
                            {{ $t("kiosk.api_logs.load_more") }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div
                v-else-if="!isInitialLoad"
                class="bg-white rounded-xl border-iron-200 py-20 border text-center"
            >
                <div
                    class="w-12 h-12 rounded-xl bg-iron-100 mb-4 mx-auto flex items-center justify-center"
                >
                    <Database class="h-5 w-5 text-iron-400" />
                </div>
                <p class="text-sm font-medium text-iron-600">
                    {{ $t("kiosk.api_logs.empty") }}
                </p>
                <p class="text-xs text-iron-400 mt-1">
                    {{ $t("kiosk.api_logs.empty_hint") }}
                </p>
            </div>
        </div>

        <!-- Detail Dialog -->
        <Dialog v-model:open="detailOpen">
            <DialogContent class="max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{{
                        $t("kiosk.api_logs.detail.title")
                    }}</DialogTitle>
                    <DialogDescription>
                        <span class="font-mono text-xs">{{
                            selectedEntry?.correlation_id || "—"
                        }}</span>
                    </DialogDescription>
                </DialogHeader>

                <div v-if="selectedEntry" class="px-6 pt-4 pb-6 space-y-5">
                    <!-- Context Cards -->
                    <div
                        v-if="contextLoading"
                        class="gap-2 text-xs text-iron-400 flex items-center"
                    >
                        <Loader2 class="h-3 w-3 animate-spin" />
                        {{ $t("kiosk.api_logs.context.loading") }}
                    </div>

                    <div
                        v-if="
                            contextData &&
                            (contextData.event_type ||
                                contextData.reservation ||
                                contextData.event_type_group)
                        "
                        class="gap-3 flex flex-wrap"
                    >
                        <!-- Event Type Card -->
                        <div
                            v-if="contextData.event_type"
                            class="bg-iron-50 border-iron-100 rounded-lg px-3.5 py-2.5 min-w-[200px] flex-1 border"
                        >
                            <div class="gap-1.5 mb-1.5 flex items-center">
                                <Calendar class="h-3 w-3 text-iron-400" />
                                <span
                                    class="font-semibold text-iron-400 tracking-wider text-[11px] uppercase"
                                    >{{
                                        $t("kiosk.api_logs.context.event_type")
                                    }}</span
                                >
                            </div>
                            <p class="text-sm font-medium text-iron-800">
                                {{ contextData.event_type.name }}
                            </p>
                            <p class="text-xs text-iron-500 mt-0.5">
                                {{ $t("kiosk.api_logs.context.team") }}:
                                {{ contextData.event_type.team }}
                            </p>
                        </div>

                        <!-- Event Type Group Card -->
                        <div
                            v-if="contextData.event_type_group"
                            class="bg-iron-50 border-iron-100 rounded-lg px-3.5 py-2.5 min-w-[200px] flex-1 border"
                        >
                            <div class="gap-1.5 mb-1.5 flex items-center">
                                <Tag class="h-3 w-3 text-iron-400" />
                                <span
                                    class="font-semibold text-iron-400 tracking-wider text-[11px] uppercase"
                                    >{{
                                        $t(
                                            "kiosk.api_logs.context.event_type_group",
                                        )
                                    }}</span
                                >
                            </div>
                            <p class="text-sm font-medium text-iron-800">
                                {{ contextData.event_type_group.title }}
                            </p>
                            <p class="text-xs text-iron-500 mt-0.5">
                                {{ $t("kiosk.api_logs.context.team") }}:
                                {{ contextData.event_type_group.team }}
                            </p>
                        </div>

                        <!-- Reservation Card -->
                        <div
                            v-if="contextData.reservation"
                            class="bg-iron-50 border-iron-100 rounded-lg px-3.5 py-2.5 min-w-[200px] flex-1 border"
                        >
                            <div class="gap-1.5 mb-1.5 flex items-center">
                                <User class="h-3 w-3 text-iron-400" />
                                <span
                                    class="font-semibold text-iron-400 tracking-wider text-[11px] uppercase"
                                    >{{
                                        $t("kiosk.api_logs.context.reservation")
                                    }}</span
                                >
                            </div>
                            <div class="gap-2 flex items-center">
                                <p class="text-sm font-medium text-iron-800">
                                    {{
                                        [
                                            contextData.reservation.firstname,
                                            contextData.reservation.lastname,
                                        ]
                                            .filter(Boolean)
                                            .join(" ") || "—"
                                    }}
                                </p>
                                <Badge
                                    variant="outline"
                                    class="px-1.5 py-0 text-[10px]"
                                    >{{ contextData.reservation.status }}</Badge
                                >
                            </div>
                            <p class="text-xs text-iron-500 mt-0.5">
                                {{ contextData.reservation.email }}
                            </p>
                            <p
                                v-if="contextData.reservation.start"
                                class="text-xs text-iron-400 mt-0.5 font-mono"
                            >
                                {{
                                    formatTimestamp(
                                        contextData.reservation.start,
                                    )
                                }}
                            </p>
                        </div>
                    </div>

                    <!-- Summary bar -->
                    <div class="gap-3 flex flex-wrap items-center">
                        <span
                            :class="methodPillClass(selectedEntry.method)"
                            class="font-bold px-2 rounded tracking-wide py-[3px] text-[11px]"
                            >{{ selectedEntry.method }}</span
                        >
                        <span
                            v-if="selectedEntry.status"
                            :class="statusPillClass(selectedEntry.status)"
                            class="font-bold px-2 rounded-full py-[3px] text-[11px] tabular-nums"
                            >{{ selectedEntry.status }}</span
                        >
                        <span
                            v-if="selectedEntry.duration_ms"
                            class="text-xs text-iron-500 font-mono"
                            >{{ selectedEntry.duration_ms }}ms</span
                        >
                    </div>

                    <!-- URL -->
                    <div>
                        <p
                            class="font-medium text-iron-400 tracking-wider mb-1.5 text-[11px] uppercase"
                        >
                            URL
                        </p>
                        <p
                            class="text-sm text-iron-800 font-mono leading-relaxed bg-iron-50 rounded-lg px-3 py-2 break-all"
                        >
                            {{ selectedEntry.url }}
                        </p>
                    </div>

                    <!-- Metadata Grid -->
                    <div class="gap-x-6 gap-y-4 grid grid-cols-2">
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                {{ $t("kiosk.api_logs.detail.timestamp") }}
                            </p>
                            <p class="text-sm text-iron-700 font-mono">
                                {{ selectedEntry.timestamp || "—" }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                {{ $t("kiosk.api_logs.detail.ip") }}
                            </p>
                            <p class="text-sm text-iron-700 font-mono">
                                {{ selectedEntry.ip ?? "—" }}
                            </p>
                        </div>
                        <div class="col-span-2">
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                {{ $t("kiosk.api_logs.detail.user_agent") }}
                            </p>
                            <p
                                class="text-sm text-iron-700 font-mono break-all"
                            >
                                {{ selectedEntry.user_agent ?? "—" }}
                            </p>
                        </div>
                    </div>

                    <!-- Request -->
                    <div v-if="selectedEntry.request">
                        <div class="gap-2 mb-2 flex items-center">
                            <div
                                class="w-1.5 h-1.5 bg-blue-400 rounded-full"
                            ></div>
                            <p
                                class="font-semibold text-iron-500 tracking-wider text-[11px] uppercase"
                            >
                                {{ $t("kiosk.api_logs.detail.request") }}
                            </p>
                        </div>
                        <pre
                            class="bg-iron-50 border-iron-100 rounded-lg p-3 leading-relaxed font-mono text-iron-700 max-h-60 overflow-x-auto overflow-y-auto border text-[12px]"
                            >{{ formatJson(selectedEntry.request) }}</pre
                        >
                    </div>

                    <!-- Response -->
                    <div v-if="selectedEntry.response">
                        <div class="gap-2 mb-2 flex items-center">
                            <div
                                class="w-1.5 h-1.5 bg-emerald-400 rounded-full"
                            ></div>
                            <p
                                class="font-semibold text-iron-500 tracking-wider text-[11px] uppercase"
                            >
                                {{ $t("kiosk.api_logs.detail.response") }}
                            </p>
                        </div>
                        <pre
                            class="bg-iron-50 border-iron-100 rounded-lg p-3 leading-relaxed font-mono text-iron-700 max-h-60 overflow-x-auto overflow-y-auto border text-[12px]"
                            >{{ formatJson(selectedEntry.response) }}</pre
                        >
                    </div>

                    <!-- Exception -->
                    <div v-if="selectedEntry.exception">
                        <div class="gap-2 mb-2 flex items-center">
                            <div
                                class="w-1.5 h-1.5 bg-red-400 rounded-full"
                            ></div>
                            <p
                                class="font-semibold text-red-500 tracking-wider text-[11px] uppercase"
                            >
                                {{ $t("kiosk.api_logs.detail.exception") }}
                            </p>
                        </div>
                        <pre
                            class="bg-red-50 border-red-100 rounded-lg p-3 leading-relaxed font-mono text-red-700 max-h-48 overflow-x-auto overflow-y-auto border text-[12px]"
                            >{{ formatJson(selectedEntry.exception) }}</pre
                        >
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    </app-layout>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog-v2";
import {
    Search,
    Database,
    SlidersHorizontal,
    ChevronDown,
    Loader2,
    Calendar,
    User,
    Tag,
} from "lucide-vue-next";
import axios from "axios";
import { Badge } from "@/components/ui/badge";

type ApiLogEntry = {
    correlation_id: string;
    timestamp: string;
    method: string;
    url: string;
    status: number;
    duration_ms: number;
    ip: string | null;
    token: string | null;
    token_short: string | null;
    user_agent: string | null;
    request: Record<string, unknown> | null;
    response: Record<string, unknown> | null;
    exception: Record<string, unknown> | null;
};

type ApiLogResult = {
    entries: ApiLogEntry[];
    next_token: string | null;
    total_returned: number;
};

type Filters = {
    start_date: string;
    end_date: string;
    method: string | undefined;
    status_min: string | undefined;
    status_max: string | undefined;
    url_search: string | undefined;
    token: string | undefined;
    correlation_id: string | undefined;
    message_search: string | undefined;
    next_token: string | undefined;
    user_id: number | undefined;
};

type UserInfo = {
    id: number;
    name: string;
    email: string;
};

const props = defineProps<{
    filters: Filters;
    logs: ApiLogResult;
    filterUser: UserInfo | null;
}>();

const filtersExpanded = ref(true);

type ApiRoute = {
    value: string;
    label: string;
    /** URL path used for CloudWatch search. Use {*} for wildcard params. */
    path: string;
};

const apiRoutes: ApiRoute[] = [
    {
        value: "configuration",
        label: "GET /configuration",
        path: "/configuration?*",
    },
    {
        value: "event-type-groups",
        label: "GET /event-type-groups/*",
        path: "/event-type-groups/{*}",
    },
    {
        value: "event-types-init",
        label: "POST /event-types/init",
        path: "/event-types/init",
    },
    { value: "time-slots", label: "GET /time-slots", path: "/time-slots?*" },
    {
        value: "reservations-prepare",
        label: "POST /reservations/prepare",
        path: "/reservations/prepare",
    },
    {
        value: "reservations-store",
        label: "POST /reservations",
        path: "/reservations",
    },
    {
        value: "reservations-update",
        label: "PUT /reservations/*",
        path: "/reservations/{*}",
    },
    {
        value: "reservations-confirm",
        label: "POST /reservations/*/confirm",
        path: "/reservations/{*}/confirm",
    },
    {
        value: "reservations-refresh",
        label: "POST /reservations/*/refresh",
        path: "/reservations/{*}/refresh",
    },
    {
        value: "reservations-payments",
        label: "POST /reservations/*/payments",
        path: "/reservations/{*}/payments",
    },
    { value: "countries", label: "GET /countries", path: "/countries" },
    { value: "timezones", label: "GET /timezones", path: "/timezones" },
    { value: "newsletters", label: "POST /newsletters", path: "/newsletters" },
    {
        value: "affiliate-click",
        label: "POST /affiliate-click",
        path: "/affiliate-click",
    },
    {
        value: "support-messages",
        label: "POST /support/messages",
        path: "/support/messages",
    },
];

const urlSelectValue = ref<string | undefined>(undefined);
const urlParamValue = ref("");
const customUrlValue = ref("");

const selectedRoute = computed((): ApiRoute | undefined => {
    if (!urlSelectValue.value || urlSelectValue.value === "__custom__")
        return undefined;
    return apiRoutes.find((r) => r.value === urlSelectValue.value);
});

const selectedRouteHasParam = computed(
    () => !!selectedRoute.value?.path.includes("{*}"),
);

const resolvedUrlSearch = computed((): string | undefined => {
    if (!urlSelectValue.value) return undefined;
    if (urlSelectValue.value === "__custom__") {
        return customUrlValue.value || undefined;
    }
    const route = selectedRoute.value;
    if (!route) return undefined;
    if (route.path.includes("{*}")) {
        const param = urlParamValue.value.trim();
        if (param) {
            return route.path.replace("{*}", param);
        }
        return route.path.replace("{*}", "*");
    }
    return route.path;
});

// Restore from filters on load
function initFromUrlSearch(urlSearch: string | undefined): void {
    if (!urlSearch) return;
    for (const r of apiRoutes) {
        if (r.path.includes("{*}")) {
            const regex = new RegExp(
                "^" +
                    r.path
                        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                        .replace("\\{\\*\\}", "(.+)") +
                    "$",
            );
            const match = urlSearch.match(regex);
            if (match) {
                urlSelectValue.value = r.value;
                urlParamValue.value = match[1] === "*" ? "" : match[1];
                return;
            }
        } else if (urlSearch === r.path) {
            urlSelectValue.value = r.value;
            return;
        }
    }
    urlSelectValue.value = "__custom__";
    customUrlValue.value = urlSearch;
}

initFromUrlSearch(props.filters.url_search ?? undefined);

function toDatetimeLocal(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${day}T${h}:${min}`;
}

const filterForm = reactive<Filters>({
    start_date: toDatetimeLocal(props.filters.start_date),
    end_date: toDatetimeLocal(props.filters.end_date),
    method: props.filters.method ?? undefined,
    status_min: props.filters.status_min ?? undefined,
    status_max: props.filters.status_max ?? undefined,
    url_search: props.filters.url_search ?? undefined,
    token: props.filters.token ?? undefined,
    correlation_id: props.filters.correlation_id ?? undefined,
    message_search: props.filters.message_search ?? undefined,
    next_token: undefined,
});

const activeFilterCount = computed(() => {
    let count = 0;
    if (filterForm.method) count++;
    if (filterForm.status_min) count++;
    if (filterForm.status_max) count++;
    if (resolvedUrlSearch.value) count++;
    if (filterForm.token) count++;
    if (filterForm.correlation_id) count++;
    if (filterForm.message_search) count++;
    return count;
});

type ContextEventType = {
    name: string;
    slug: string;
    team: string;
};

type ContextReservation = {
    uuid: string;
    email: string;
    firstname: string | null;
    lastname: string | null;
    status: string;
    start: string | null;
    end: string | null;
};

type ContextEventTypeGroup = {
    title: string;
    slug: string | null;
    team: string;
};

type ContextData = {
    event_type?: ContextEventType;
    reservation?: ContextReservation;
    event_type_group?: ContextEventTypeGroup;
};

const detailOpen = ref(false);
const selectedEntry = ref<ApiLogEntry | null>(null);
const contextData = ref<ContextData | null>(null);
const contextLoading = ref(false);

// Accumulated entries across pagination
const accumulatedEntries = ref<ApiLogEntry[]>([]);
const currentNextToken = ref<string | null>(null);
const isLoadingMore = ref(false);
const isInitialLoad = ref(true);

watch(
    () => props.logs,
    (newLogs) => {
        if (!newLogs) return;

        if (isLoadingMore.value) {
            accumulatedEntries.value.push(...newLogs.entries);
        } else {
            accumulatedEntries.value = [...newLogs.entries];
        }

        currentNextToken.value = newLogs.next_token;
        isLoadingMore.value = false;
        isInitialLoad.value = false;
    },
    { deep: true },
);

function localDatetimeToIso(datetimeLocal: string): string {
    const d = new Date(datetimeLocal);
    if (isNaN(d.getTime())) return datetimeLocal;
    return d.toISOString();
}

function buildBaseParams(): Record<string, string> {
    const params: Record<string, string> = {};

    if (filterForm.start_date)
        params.start_date = localDatetimeToIso(filterForm.start_date);
    if (filterForm.end_date)
        params.end_date = localDatetimeToIso(filterForm.end_date);
    if (filterForm.method) params.method = filterForm.method;
    if (filterForm.status_min) params.status_min = filterForm.status_min;
    if (filterForm.status_max) params.status_max = filterForm.status_max;
    if (resolvedUrlSearch.value) params.url_search = resolvedUrlSearch.value;
    if (filterForm.token) params.token = filterForm.token;
    if (filterForm.correlation_id)
        params.correlation_id = filterForm.correlation_id;
    if (filterForm.message_search)
        params.message_search = filterForm.message_search;
    if (props.filterUser) params.user_id = String(props.filterUser.id);

    return params;
}

function applyFilters(): void {
    isInitialLoad.value = true;
    isLoadingMore.value = false;
    accumulatedEntries.value = [];
    currentNextToken.value = null;

    router.get(route("app.kiosk.api-logs.index"), buildBaseParams(), {
        preserveState: true,
    });
}

function clearFilters(): void {
    isInitialLoad.value = true;
    isLoadingMore.value = false;
    accumulatedEntries.value = [];
    currentNextToken.value = null;

    filterForm.method = undefined;
    filterForm.status_min = undefined;
    filterForm.status_max = undefined;
    filterForm.url_search = undefined;
    urlSelectValue.value = undefined;
    urlParamValue.value = "";
    customUrlValue.value = "";
    filterForm.token = undefined;
    filterForm.correlation_id = undefined;
    filterForm.message_search = undefined;
    filterForm.next_token = undefined;

    const params: Record<string, string> = {
        start_date: localDatetimeToIso(filterForm.start_date),
        end_date: localDatetimeToIso(filterForm.end_date),
    };
    if (props.filterUser) params.user_id = String(props.filterUser.id);

    router.get(route("app.kiosk.api-logs.index"), params, {
        preserveState: true,
    });
}

function loadMore(): void {
    if (!currentNextToken.value) return;

    isLoadingMore.value = true;

    const params = buildBaseParams();
    params.next_token = currentNextToken.value;

    router.get(route("app.kiosk.api-logs.index"), params, {
        preserveState: true,
        preserveScroll: true,
    });
}

const UUID_RE = /^[a-f0-9-]{36}$/;

function extractReservationUuid(entry: ApiLogEntry): string | null {
    // 1. Try URL (e.g. /reservations/{uuid}/confirm)
    const urlMatch = entry.url.match(/\/reservations\/([a-f0-9-]{36})/);
    if (urlMatch) return urlMatch[1];

    // 2. Try response body — structure is { headers, body: { data: { uuid } } }
    if (entry.response && typeof entry.response === "object") {
        const resp = entry.response as Record<string, unknown>;

        // Direct: response.body.data.uuid
        if (resp.body && typeof resp.body === "object") {
            const body = resp.body as Record<string, unknown>;
            if (body.data && typeof body.data === "object") {
                const data = body.data as Record<string, unknown>;
                if (typeof data.uuid === "string" && UUID_RE.test(data.uuid)) {
                    return data.uuid;
                }
            }
        }

        // Fallback: response.data.uuid (in case body wrapper is stripped)
        if (resp.data && typeof resp.data === "object") {
            const data = resp.data as Record<string, unknown>;
            if (typeof data.uuid === "string" && UUID_RE.test(data.uuid)) {
                return data.uuid;
            }
        }

        // Fallback: response.uuid
        if (typeof resp.uuid === "string" && UUID_RE.test(resp.uuid)) {
            return resp.uuid;
        }
    }

    return null;
}

function extractEventTypeGroupToken(entry: ApiLogEntry): string | null {
    const match = entry.url.match(/\/event-type-groups\/([^/?]+)/);
    return match ? match[1] : null;
}

function openDetail(entry: ApiLogEntry): void {
    selectedEntry.value = entry;
    contextData.value = null;
    detailOpen.value = true;

    const params: Record<string, string> = {};
    if (entry.token) {
        params.token = entry.token;
    }
    const reservationUuid = extractReservationUuid(entry);
    if (reservationUuid) {
        params.reservation_uuid = reservationUuid;
    }
    const eventTypeGroupToken = extractEventTypeGroupToken(entry);
    if (eventTypeGroupToken) {
        params.event_type_group_token = eventTypeGroupToken;
    }

    if (Object.keys(params).length > 0) {
        contextLoading.value = true;
        axios
            .get(route("app.kiosk.api-logs.context"), { params })
            .then((response) => {
                contextData.value = response.data;
            })
            .finally(() => {
                contextLoading.value = false;
            });
    }
}

function formatTimestamp(timestamp: string): string {
    if (!timestamp) return "—";
    try {
        const d = new Date(timestamp);
        if (isNaN(d.getTime())) return timestamp;
        return d.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return timestamp;
    }
}

function formatUrl(url: string): string {
    if (!url) return "—";
    try {
        const u = new URL(url);
        return u.pathname + u.search;
    } catch {
        return url;
    }
}

function formatJson(data: unknown): string {
    return JSON.stringify(data, null, 2);
}

function methodPillClass(method: string): string {
    const classes: Record<string, string> = {
        GET: "bg-blue-50 text-blue-600",
        POST: "bg-emerald-50 text-emerald-600",
        PUT: "bg-amber-50 text-amber-600",
        PATCH: "bg-orange-50 text-orange-600",
        DELETE: "bg-red-50 text-red-600",
    };
    return classes[method] ?? "bg-iron-50 text-iron-600";
}

function statusPillClass(status: number): string {
    if (status >= 500) return "bg-red-100 text-red-700";
    if (status >= 400) return "bg-orange-100 text-orange-700";
    if (status >= 300) return "bg-blue-100 text-blue-700";
    return "bg-emerald-100 text-emerald-700";
}
</script>
